import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/layout/Layout';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { MapPin, Plus, Edit, ArrowLeft, Loader2, Building2, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import { clearMMDCache } from '../../hooks/useMMDLocations';

// All available grades
const ALL_GRADES = ['MEO Class 1', 'MEO Class 2', 'MEO Class 4', 'ETO', 'Master', 'Chief Mate', '2MFG'];

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MMDManagementPage = () => {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    city: '',
    state: '',
    grades: [...ALL_GRADES],
    is_active: true,
    sort_order: ''
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/mmd/locations`);
      setLocations(response.data.locations || []);
    } catch (error) {
      console.error('Failed to fetch MMD locations:', error);
      toast.error('Failed to load MMD locations');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      city: '',
      state: '',
      grades: [...ALL_GRADES],
      is_active: true,
      sort_order: ''
    });
  };

  const openCreateDialog = () => {
    setEditMode(false);
    setCurrentLocation(null);
    resetForm();
    // Set next sort order
    const maxOrder = locations.reduce((max, loc) => Math.max(max, loc.sort_order || 0), 0);
    setFormData(prev => ({ ...prev, sort_order: (maxOrder + 1).toString() }));
    setDialogOpen(true);
  };

  const openEditDialog = (location) => {
    setEditMode(true);
    setCurrentLocation(location);
    // Ensure grades is always an array
    const locationGrades = Array.isArray(location.grades) ? location.grades : [...ALL_GRADES];
    setFormData({
      name: location.name || '',
      code: location.code || '',
      city: location.city || '',
      state: location.state || '',
      grades: locationGrades,
      is_active: location.is_active !== false,
      sort_order: location.sort_order?.toString() || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.code.trim() || !formData.city.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.grades.length === 0) {
      toast.error('Please select at least one grade');
      return;
    }
    
    try {
      setSaving(true);
      const payload = {
        ...formData,
        sort_order: formData.sort_order ? parseInt(formData.sort_order) : null,
        state: formData.state || null
      };
      
      if (editMode) {
        await axios.put(`${API}/admin/mmd/locations/${currentLocation.id}`, payload);
        toast.success('MMD location updated successfully');
      } else {
        await axios.post(`${API}/admin/mmd/locations`, payload);
        toast.success('MMD location created successfully');
      }
      
      setDialogOpen(false);
      resetForm();
      clearMMDCache(); // Clear cache so other components fetch fresh data
      fetchLocations();
    } catch (error) {
      console.error('Failed to save MMD location:', error);
      toast.error(error.response?.data?.detail || 'Failed to save MMD location');
    } finally {
      setSaving(false);
    }
  };

  const handleGradeToggle = (grade) => {
    setFormData(prev => {
      const currentGrades = Array.isArray(prev.grades) ? prev.grades : [];
      const newGrades = currentGrades.includes(grade)
        ? currentGrades.filter(g => g !== grade)
        : [...currentGrades, grade];
      return { ...prev, grades: newGrades };
    });
  };

  const handleSelectAllGrades = () => {
    setFormData(prev => {
      const currentGrades = Array.isArray(prev.grades) ? prev.grades : [];
      return {
        ...prev,
        grades: currentGrades.length === ALL_GRADES.length ? [] : [...ALL_GRADES]
      };
    });
  };

  const handleToggleActive = async (location) => {
    try {
      await axios.put(`${API}/admin/mmd/locations/${location.id}`, {
        is_active: !location.is_active
      });
      toast.success(`MMD ${location.is_active ? 'deactivated' : 'activated'} successfully`);
      clearMMDCache(); // Clear cache so other components fetch fresh data
      fetchLocations();
    } catch (error) {
      console.error('Failed to toggle MMD status:', error);
      toast.error('Failed to update MMD status');
    }
  };

  const activeCount = locations.filter(l => l.is_active).length;
  const inactiveCount = locations.filter(l => !l.is_active).length;

  return (
    <Layout>
      <div className="page-transition">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/content')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Content Management
        </Button>
        
        {/* Header */}
        <Card className="mb-6">
          <CardHeader className="bg-gradient-to-r from-[#F59E0B]/5 to-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-[#F59E0B] rounded-lg">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-2xl">MMD Management</CardTitle>
                  <CardDescription>Manage Mercantile Marine Department locations across India</CardDescription>
                </div>
              </div>
              <Button onClick={openCreateDialog} className="bg-[#F59E0B] hover:bg-[#F59E0B]/90">
                <Plus className="w-4 h-4 mr-2" /> Add MMD
              </Button>
            </div>
          </CardHeader>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-[#F59E0B]">{locations.length}</p>
              <p className="text-sm text-gray-600">Total MMDs</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{activeCount}</p>
              <p className="text-sm text-gray-600">Active</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-gray-400">{inactiveCount}</p>
              <p className="text-sm text-gray-600">Inactive</p>
            </CardContent>
          </Card>
        </div>

        {/* Locations Table */}
        <Card>
          <CardContent className="p-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#F59E0B]" />
              </div>
            ) : locations.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No MMD locations found</p>
                <Button onClick={openCreateDialog} className="mt-4 bg-[#F59E0B] hover:bg-[#F59E0B]/90">
                  <Plus className="w-4 h-4 mr-2" /> Add First MMD
                </Button>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="w-16">#</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>City</TableHead>
                      <TableHead>Grades</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {locations.map((location, index) => (
                      <TableRow key={location.id} className={!location.is_active ? 'opacity-60' : ''}>
                        <TableCell className="font-mono text-gray-500">
                          {location.sort_order || index + 1}
                        </TableCell>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-[#F59E0B]" />
                            {location.name}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-mono">
                            {location.code}
                          </Badge>
                        </TableCell>
                        <TableCell>{location.city}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 max-w-[200px]">
                            {(location.grades || ALL_GRADES).length === ALL_GRADES.length ? (
                              <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                                All Grades
                              </Badge>
                            ) : (
                              (location.grades || []).slice(0, 2).map(grade => (
                                <Badge key={grade} variant="secondary" className="text-xs">
                                  {grade}
                                </Badge>
                              ))
                            )}
                            {(location.grades || []).length > 2 && (location.grades || []).length < ALL_GRADES.length && (
                              <Badge variant="outline" className="text-xs">
                                +{(location.grades || []).length - 2}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Switch
                            checked={location.is_active}
                            onCheckedChange={() => handleToggleActive(location)}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(location)}
                            className="h-8 w-8 p-0 text-[#F59E0B]"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit MMD Location' : 'Add MMD Location'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Mumbai MMD"
                  required
                  className="mt-1"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="code">Code *</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="e.g., MUM"
                    maxLength={11}
                    required
                    className="mt-1 font-mono"
                  />
                </div>
                <div>
                  <Label htmlFor="sort_order">Sort Order</Label>
                  <Input
                    id="sort_order"
                    type="number"
                    min="1"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
                    placeholder="Auto"
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="e.g., Mumbai"
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    placeholder="e.g., Maharashtra"
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <Label>Active Status</Label>
                  <p className="text-xs text-gray-500">Inactive MMDs will not appear in forms</p>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
              </div>

              {/* Grades Selection */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-[#F59E0B]" />
                    Supported Grades *
                  </Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleSelectAllGrades}
                    className="text-xs h-6 text-[#F59E0B]"
                  >
                    {(formData.grades || []).length === ALL_GRADES.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2 p-3 bg-gray-50 rounded-lg max-h-[180px] overflow-y-auto">
                  {ALL_GRADES.map((grade) => (
                    <div
                      key={grade}
                      className="flex items-center space-x-2 p-2 rounded hover:bg-white cursor-pointer"
                    >
                      <Checkbox
                        id={`grade-${grade}`}
                        checked={(formData.grades || []).includes(grade)}
                        onCheckedChange={() => handleGradeToggle(grade)}
                      />
                      <label
                        htmlFor={`grade-${grade}`}
                        className="text-sm cursor-pointer select-none flex-1"
                      >
                        {grade}
                      </label>
                    </div>
                  ))}
                </div>
                {(formData.grades || []).length === 0 && (
                  <p className="text-xs text-red-500">Please select at least one grade</p>
                )}
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setDialogOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-[#F59E0B] hover:bg-[#F59E0B]/90"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editMode ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default MMDManagementPage;
